import React, { useContext, useMemo } from 'react';
import moment from 'moment';
import { Alert, Box, Header, StatusIndicator, Table } from '@amzn/awsui-components-react';
import { SegmentRow } from 'src/models/segment-cdt/SegmentCdtTypes';
import { DerivedAllocationSheetBuilderContext } from 'src/services/derived-allocation-sheet/DerivedAllocationSheetBuilderContext';
import { MappingRowStatus, mappingRowStatus } from 'src/services/derived-allocation-sheet/dasMappingPreview';
import { latestPeriod } from 'src/services/segment-cdt/segmentPeriods';

/** Rows the preview reads; the segment table detail page shows all of them. */
export const PREVIEW_PAGE_SIZE = 50;

const formatPeriod = (period: string) => moment(period, 'YYYYMM').format('MMM YYYY');

interface PreviewRow { row: SegmentRow, groupName?: string, status: MappingRowStatus }

/** Names that match no Step 3 group come first, as they are the ones to fix; the order is otherwise kept. */
const STATUS_ORDER = [MappingRowStatus.UNKNOWN_GROUP, MappingRowStatus.NOT_MAPPED, MappingRowStatus.MAPPED];

function RowStatus({ status, groupName }: { status: MappingRowStatus, groupName?: string }) {
    switch (status) {
        case MappingRowStatus.MAPPED:
            return <StatusIndicator type="success">Maps to a Step 3 group</StatusIndicator>;
        case MappingRowStatus.UNKNOWN_GROUP:
            return <StatusIndicator type="warning">{`No Step 3 group named ${groupName?.trim()}`}</StatusIndicator>;
        default:
            return <StatusIndicator type="stopped">No group</StatusIndicator>;
    }
}

/**
 * Read-only preview of a Step 4 mapping: the segments of the picked table in its newest period with rows, the
 * group each one names in the picked column, and whether that group exists in Step 3. Checks only the first
 * {@link PREVIEW_PAGE_SIZE} segments and says so; the segment table's own page shows the rest. Until the Step 3
 * group names arrive (`groupNames` unset), the preview shows as loading rather than flagging every name as unknown.
 */
export default function DasMappingPreview(props: { tableId: string, columnId: string, groupNames?: string[] }) {
    const { tableId, columnId, groupNames } = props;
    const { services } = useContext(DerivedAllocationSheetBuilderContext);
    const tables = services.segmentDataTablesService;
    const [periodsResult, periodsLoading, periodsError] = tables.getSegmentPeriods(tableId);
    const period = latestPeriod(periodsResult?.periods ?? []);
    const request = useMemo(() => (period ? { tableId, period, page: 1, pageSize: PREVIEW_PAGE_SIZE } : undefined), [tableId, period]);
    const [rowsResult, rowsLoading, rowsError] = tables.searchSegmentDataTableRows(request);
    // A page of an earlier pick can still be on screen while the new one loads.
    const rows = useMemo(() => (rowsResult?.tableId === tableId && rowsResult?.period === period ? rowsResult?.rows ?? [] : []),
        [rowsResult, tableId, period]);
    const previewRows: PreviewRow[] = useMemo(() => rows
        .map((row) => {
            const groupName = row.textValues?.[columnId];
            return { row, groupName, status: mappingRowStatus(groupName, groupNames ?? []) };
        })
        .sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status)), [rows, columnId, groupNames]);
    const mappedCount = previewRows.filter((r) => r.status === MappingRowStatus.MAPPED).length;
    const total = rowsResult?.totalSize ?? rows.length;

    const error = periodsError || rowsError;
    const waitingForRows = periodsLoading || rowsLoading || (!!period && rowsResult == null);
    const waitingForGroups = groupNames == null;
    const loading = !error && (waitingForRows || waitingForGroups);

    let description = 'The table has no rows yet.';
    if (period && total > rows.length) {
        description = `${formatPeriod(period)}: checked the first ${rows.length} of ${total} segments, and ${mappedCount} of them map to a Step 3 group.`;
    } else if (period) {
        description = `${formatPeriod(period)}: ${mappedCount} of ${rows.length} segments map to a Step 3 group.`;
    }

    return (
        <div data-class="dasMappingPreview">
            {!!error && <Alert type="error" header="Unable to load the mapping preview">{error}</Alert>}
            <Table
                variant="embedded"
                loading={loading}
                loadingText="Loading the mapping"
                items={previewRows}
                trackBy={(r: PreviewRow) => r.row.segmentValue ?? ''}
                header={<Header variant="h3" description={loading || error ? undefined : description}>Mapping preview</Header>}
                // A failed read shows only its alert, not an empty table that says the period has no segments.
                empty={error ? null : <Box textAlign="center" color="inherit">{period ? 'No segments in this period' : 'The table has no rows yet'}</Box>}
                columnDefinitions={[
                    { id: 'segment', header: 'Segment', cell: (r: PreviewRow) => r.row.segmentValue },
                    { id: 'description', header: 'Description', cell: (r: PreviewRow) => r.row.segmentDescription || '-' },
                    { id: 'group', header: 'Allocation group', cell: (r: PreviewRow) => r.groupName?.trim() || '-' },
                    { id: 'status', header: 'Status', cell: (r: PreviewRow) => <RowStatus status={r.status} groupName={r.groupName} /> },
                ]}
            />
        </div>
    );
}
